import React, { PropsWithChildren, ReactNode } from 'react';
import { Button, Flex } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { StyledEditableTileContainer } from './styled';
import { StyledActionContainer } from '@dealstack/frontend/shared/components/EditableTileContainer/styled.tsx';

interface Props {
	hasEditPermission?: boolean;
	onEditClick?: () => void;
	onDeleteClick?: () => void;
	dataTestId?: string;
	deleteBtnDataTestId?: string;
	editBtnDataTestId?: string;
	withBorder?: boolean;
	backgroundColor?: string;
	customContent?: ReactNode;
}

export const EditableTileContainer = ({
	children,
	hasEditPermission,
	onEditClick,
	onDeleteClick,
	dataTestId,
	deleteBtnDataTestId,
	editBtnDataTestId,
	withBorder,
	backgroundColor,
	customContent,
}: PropsWithChildren<Props>) => {
	const handleEditClick = () => {
		if (hasEditPermission) {
			onEditClick?.();
		}
	};

	const handleDeleteClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (hasEditPermission) {
			onDeleteClick?.();
		}
	};

	return (
		<StyledEditableTileContainer
			p="0.5rem"
			gap="0.5rem"
			align="self-start"
			justify="space-between"
			w="100%"
			$hasEditPermission={hasEditPermission}
			$withBorder={withBorder}
			$backgroundColor={backgroundColor}
			data-testid={dataTestId}
		>
			<Flex flex={1}>{children}</Flex>
			{(hasEditPermission || !!customContent) && (
				<Flex direction="column" align="flex-end" gap="0.25rem">
					{hasEditPermission && (
						<StyledActionContainer gap="0.25rem">
							{onDeleteClick && (
								<Button
									variant="transparent"
									bg="gray.2"
									c="red.6"
									size="xs"
									onClick={handleDeleteClick}
									data-testid={deleteBtnDataTestId}
								>
									<Icon variant="trash-can" size="lg" />
								</Button>
							)}
							{onEditClick && (
								<Button
									variant="transparent"
									bg="gray.2"
									c="dark.8"
									size="xs"
									onClick={handleEditClick}
									data-testid={editBtnDataTestId}
								>
									<Icon variant="pencil" size="lg" />
								</Button>
							)}
						</StyledActionContainer>
					)}
					{customContent}
				</Flex>
			)}
		</StyledEditableTileContainer>
	);
};
